import { InterviewArticleType } from 'components/v2/molecules/article-interview';
import { ArticleCategoryEnum, ArticleTypeEnum } from 'service/article';

const data: InterviewArticleType = {
  id: 'interview-kabuzaru',
  title: '投資は「才能」ではなく「習慣」で勝つ',
  breadcrumbItems: [
    { label: 'ホーム', href: '/about' },
    { label: 'クリエイター', href: '/about/creator' },
    {
      label: '投資は「才能」ではなく「習慣」で勝つ',
    },
  ],
  isHidingCreatorNameAboveImage: false,
  excerpt: `「仮説と思考の積み重ね」が資産を育てる`,
  category: ArticleCategoryEnum.Creator,
  type: ArticleTypeEnum.ForCreator,
  date: '2025年12月18日',
  thumbnail: 'https://assets.postprime.com/internals/kabuzaru-profile.jpg',
  href: '/about/creator/interview-kabuzaru',
  metadata: {
    title: 'クリエイター成功事例 かぶざる | PostPrime - 投資家の成長ストーリー',
    description:
      'PostPrimeクリエイターかぶざるの資産形成を始めてまず行ったことや株式が上手くいかない時期、転機となったきっかけなど投資の挫折から成功までのストーリーを掲載しています。成功ストーリーを確認し、自身の投資の勉強やスタイルの参考にしてみてください。',
    image: 'https://assets.postprime.com/internals/kabuzaru-profile.jpg',
    url: 'https://postprime.com/about/creator/interview-kabuzaru',
    keywords:
      'PostPrime, かぶざる, クリエイターインタビュー, 投資家, 資産形成, 投資習慣, ファンダメンタルズ分析, コミュニティ',
  },
  readTime: '15分',
  author: {
    name: 'Trung(ちゅん)',
    avatar: 'https://assets.postprime.com/internals/trung.jpg',
    jobTitle: 'CEO',
  },
  content: '',

  // Interview-specific data
  interviewLabel: 'Creator Interview Vol.3',
  heroSubtitle: '「仮説と思考の積み重ね」が資産を育てる',
  leadText: `新卒で証券会社に入社し、多くの投資家が企業を調べずに投資している現実を目の当たりにした。
徹底的なファンダメンタルズ分析と「調べる力」を武器に、30歳を超えて億り人が見えてくるところまで資産を育ててきた。
投資は「感情」ではなく「理由」で動くもの。
かぶざるの投資哲学とPostPrimeへの想いを聞いた。`,

  pullQuote: `相場を学ぶ場所であり、自分を磨く習慣そのものです`,
  pullQuoteIndex: 3,
  creatorProfile: {
    name: 'かぶざる',
    title: 'PostPrime トップクリエイター',
    avatar: 'https://assets.postprime.com/internals/kabuzaru-profile.jpg',
    profileUrl: 'https://postprime.com/srr22',
    avatarPortraitHeight: 500,
  },

  questions: [
    {
      id: 'q1',
      questionNumber: '1',
      questionText: '学びの道のり',
      answerText: `
        <p>私は新卒で証券会社に入社しました。そこで最初に感じたのは、「多くのお客様が、企業の中身をほとんど調べずに銘柄を買っている」という現実でした。証券マンに勧められるままに株を購入し、その後、売るタイミングを逃して何年、いや何十年も「塩漬け」にしてしまう——そんなお客様が驚くほど多かったのです。</p>
        <p>考えてみてください。車を買うとき、家を買うとき、販売員にすすめられたものを何も考えずに買うでしょうか？燃費、乗り心地、デザイン、シートの質感。家なら間取り、日当たり、水回り、立地。誰もが「自分のこだわり」を持ち、時間をかけて比較検討し、納得して購入を決めます。</p>
        <p>ところが、こと投資になると、それをしない人が圧倒的に多い。私はそこに、個人投資家の課題があると感じています。</p>
        <p>なぜ人は、投資になると「調べる」ことをやめてしまうのか。それは、損をするリスクが「可視化されすぎる」からです。車や家の失敗は見えにくいけれど、株の損失は数字でハッキリ突きつけられる。だからこそ、「損をしたら誰かのせいにしたい」「何を見ればいいのかわからない」という心理が働き、結果として「調べることを放棄」してしまうのです。</p>
        <p>一方で、投資で勝てている人たちはまったく違います。彼らは、その銘柄を隅々まで調べ尽くし、当時証券マンだった私に対して、逆にその企業を説明してくるほどでした。業績、事業構造、競合環境、IR資料、経営者の発言――すべてを読み込み、自分の中で「確信」と「シナリオ」を持って投資している。だからこそ、業績や投資家心理の「先行き」を読むことができるのです。</p>
        <p>私自身も、そうした経験を経て、徹底的にファンダメンタルズ分析に時間を割くようになりました。「なぜこの銘柄が良いのか」「なぜ今買うのか」。自分なりの観点を30〜50項目ほど設け、1つずつ丁寧に検証していきます。そして「買う理由」が明確に揃ったときにだけ投資を行う。</p>
        <p>逆に、保有中の銘柄についても、その理由が崩れた瞬間——たとえば「営業利益が前年比10％以上の成長を継続している」という前提が崩れたときには、迷わず売却します。</p>
        <p>投資とは、「感情」ではなく「理由」で動くもの。「調べる力」があるかどうかが、結果を分ける最大のポイントだと、私は確信しています。</p>
        <p>これから語ることを実践してきた結果、30歳を超えて、億り人が見えてくるところまで資産を育ててきました。皆さんも実践できる内容だと思います。</p>
      `,
    },
    {
      id: 'q2',
      questionNumber: '2',
      questionText: '投資においてのルーティーン',
      answerText: `
        <p>私の1日は、米国市場の動向チェックから始まります。主要指数や企業ニュースを、日経新聞・Bloombergなど3〜5の媒体で横断的に確認し、「何が相場を動かしたのか」「どんな事象が次のカタリストになり得るのか」を探します。</p>
        <p>特定のニュースだけでなく、為替や金利、セクターごとの資金の流れも含めて俯瞰的に把握します。</p>
        <p>夕刻には日本市場を同様にチェックし、主要指数や個別企業の動きを分析。米国市場と日本市場で起きた事象や、材料と相場の因果関係を整理する時間を大切にしています。</p>
        <p>夜には保有銘柄のニュースやIRを精査し、「買った理由」に変化がないか、業績見通しや市場期待に乖離がないかを確認。もし新たなカタリストを見つけた場合には、慎重にリサーチし、同業他社との競争優位を比較検証します。そのうえで優位性が確認できれば、打診買いでポジションを取ることもあります。</p>
        <p>相場を追うとは、日々市場と対話を重ねることだと感じています。</p>
      `,
    },
    {
      id: 'q3',
      questionNumber: '3',
      questionText: '動画配信において大切にしていること',
      answerText: `
        <p>私の視聴者の方は感じていらっしゃると思いますが、私が最も意識しているのは「難しい出来事を、誰にでもわかる言葉で伝えること」です。出来事をそのまま伝えるだけなら、言葉を話せる人なら誰にでもできます。</p>
        <p>大切なのは、事象を読み解き、「なぜそうなったのか」「これからどうなるのか」「背景には何があるのか」を整理し、言葉から構図や商流が自然に浮かぶように伝えることです。</p>
        <p>私は常に、専門用語をかみ砕き、理解できる表現を心がけています。また、単なる「予想」ではなく「シナリオ」で語ることも重視しています。</p>
        <p>たとえばFRBが利下げした場合・しなかった場合、それぞれでセクターや資金の流れがどう変わるのか、3つ程度の可能性を描き出す。相場に100％は存在しません。だからこそ、多面的なシナリオを持つことが、冷静な投資判断につながるのです。</p>
      `,
    },
    {
      id: 'q4',
      questionNumber: '4',
      questionText: '投資手法に関して',
      answerText: `
        <p>私たち投資家が選べる銘柄は、米国約5,000、日本約4,000、計9,000銘柄にも及びます。すべてを分析するのは不可能です。だからこそ私は、まずスクリーニングを行い、「次の30年を見据えたテーマ」に資本が流れやすい米国市場の構造に注目します。</p>
        <p>特に重視しているのは、IT・AI・クラウド・半導体など成長テーマを"裏で支える中間材・インフラ企業"。主役よりも構造を下支えする企業にこそ、割安で持続的な成長余地があると考えます。企業のポジション（上流・中間・下流）や競争優位、テーマ消失後のレジリエンスを分析し、長期視点で投資対象を選定します。</p>
        <p>AI革命を牽引したNVIDIAの成長に伴い、Super MicroやVertivなどの"AIインフラ銘柄"が急伸したのもその構造的連鎖の一例です。</p>
        <p>一方で日本株に関しては、「情報優位」こそ最大の武器です。日本語で一次情報を読み取れる強みを活かし、決算短信やIR、経営者の言葉から企業の「温度感」まで把握します。</p>
        <p>銘柄選定は、四季報を軸に独自スクリーニングをかけ、まず60〜80銘柄に絞り込み、国策や海外マネーの動向、市場成長性を精査。最終的に5〜15銘柄を選定し、四半期ごとに見直します。例えば、東京エレクのような主役ではなく、その裏で支える装置・素材・検査・冷却などの「2.5番手企業」を狙います。日本株が爆発的に伸びるのは、成長テーマが実績として業績に反映し始めた中型企業が、割安・円安・政策追い風・経営改革のタイミングで噛み合うときだからです。だからこそ、地道な情報収集と仮説検証の積み重ねは必須なのです。</p>
      `,
    },
    {
      id: 'q5',
      questionNumber: '5',
      questionText:
        'PostPrimeを含めた自身の投資コミュニティ(コミュニケーション)で気を付けていることはありますか？',
      answerText: `
        <p>私の配信で大切にしているのは、一方的に話すのではなく、視聴者の皆さんと「相場を一緒に考える場」にすることです。ライブ配信の魅力は、リアルタイムでコメントを通じて意見を交わし合えること。私は必ず全員のコメントに目を通し、すべて読み上げながら自分の考えを伝えます。</p>
        <p>その中で、自分にはなかった視点や気づきを得ることが多く、人間が陥りやすい「都合の良い解釈」を正すきっかけにもなっていまし、視聴者の皆様にもそのような場にしてほしいと思っています。だからこそ、コメントしやすい空気づくりと、考えを促す投げかけを心がけています。</p>
        <p>また、PostPrimeのサービス開始当初から、毎日相場を端的にまとめる投稿を続けています。現在はPrimeプラスで、1日2本、（相場の振り返りと短期～中期の私の考え、それに伴うセクター銘柄のシナリオ）の記事を投稿しています。</p>
        <p>毎日、お仕事やプライベートなどでお忙しい皆さんが、短い時間で何があったのか、相場がどう動いてるのキャッチアップできることを意識して投稿を続けています。</p>
      `,
    },
    {
      id: 'q6',
      questionNumber: '6',
      questionText:
        'PostPrimeの活動や投資において思い入れが高いエピソードがあれば教えてください',
      answerText: `
        <p>PostPrimeでの活動を振り返って印象に残っているのは、サービスローンチからわずか3か月でフォロワーが1万人に到達し、その勢いのまま高橋ダンさんとコラボできたことです。まだ自分の名前も知られていなかった時期に、投資への姿勢や未来への視点、そしてPostPrimeというプラットフォームの理念について語り合えたことは、今の活動を支える大きな礎になりました。</p>
        <p>また、2年連続でクリエイター賞を受賞できたことも大きな励みでした。大人になると「表彰される機会」はそう多くありませんが、努力を続ければ必ず誰かが見てくれている――その実感を与えてくれた出来事でした。</p>
        <p>さらに、活動を通じて多くの投資家仲間と出会えたことも大きな財産です。普段の生活では交わることのない方々とつながり、視野や価値観が広がっていく。この人とのつながりこそ、私にとってPostPrimeで得た最大の収穫だと感じています。</p>
        <p>そして、私の視聴者の皆様から「利益が出ました」「大きく勝てました」「参考にして上手くいきました」という報告をいただいた時が、何にも変え難い幸せを感じれる瞬間です。</p>
      `,
    },
  ],

  keyInsights: {
    title: '実践する投資の3原則',
    items: [
      {
        icon: '📊',
        title: 'チャートとファンダで見抜く「伸びる企業」',
        description:
          '投資の基本は「良い会社を、良いトレンドで、適正な価格で買う」ことに尽きます。株価は未来の期待を映す鏡であり、右肩上がりや明確なV字回復のチャートを描く銘柄が理想的です。売上と利益がともに10％以上伸び、営業利益率が10％を超える企業は、構造的な競争優位を持ちます。理論株価は正解ではなく座標軸です。いまの株価がその座標のどこにいるかを知ることで、過熱でも悲観でもなく、冷静な判断ができます。',
      },
      {
        icon: '🚀',
        title: 'カタリスト思考で掴む「上がる株」',
        description:
          '株価を動かすのは「業績」ではなく「変化」です。その変化を引き起こす火種——それがカタリスト（Catalyst）です。カタリストとは株価上昇の「きっかけ」であり、テーマや国策、構造転換の中に潜む「まだ市場が織り込んでいない変化」を指します。重要なのは「割安だから買う」ではなく、「上がる理由があるか」で判断すること。市場が気づく前に、「期待と現実のギャップ」を見抜く。そのギャップが最も大きい瞬間こそ、投資家が取るべきポジションです。',
      },
      {
        icon: '💪',
        title: '投資は「才能」ではなく「習慣」で勝つ',
        description:
          '投資は特別な才能が必要な世界ではありません。大事なのは、派手な勝負よりも「日々の積み重ね」を地道に続けることです。毎朝、米国市場の動きや為替、金利、セクターごとの資金の流れを確認し、夕方には日本市場を振り返り、夜は保有銘柄の決算やIRを見直す。こうした小さな確認の積み重ねが、感情に流されず冷静に判断する力を育ててくれます。相場は思い通りに動かないものです。だからこそ、「確信」ではなく「仮説」を持つことが大切だと思います。',
      },
    ],
  },

  ctaSection: {
    title: 'かぶざるさんをフォローして\n投資の習慣を身につけよう',
    description: '毎日の市場分析と実践的な投資戦略を無料で学べます',
    buttonText: '今すぐフォローする',
    buttonUrl: 'https://postprime.com/srr22',
  },
};

export default data;
